import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  Star, LogOut, BadgeCheck, MapPin, Users, Heart, Send, Crown, Play, 
  Image, X, ChevronLeft, ChevronRight, Eye, Clock, Filter, Camera, 
  Film, Music, Mic, PenTool, Smartphone, Search, IndianRupee,
  Clapperboard, Gamepad2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import StarAvatarFallback from "@/components/StarAvatarFallback";
import { Badge } from "@/components/ui/badge";
import BottomNav from "@/components/BottomNav";
import { ProUpgradePrompt } from "@/components/BrandSubscriptionCard";
import SipStyleFilter, {
  FOLLOWERS_MIN,
  FOLLOWERS_MAX,
  BUDGET_MIN,
  BUDGET_MAX,
} from "@/components/SipStyleFilter";
import { useAuth, API } from "@/App";

const BrandHome = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [selectedTalentImageUrl, setSelectedTalentImageUrl] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCollabModal, setShowCollabModal] = useState(false);
  const [collaborateMessage, setCollaborateMessage] = useState("");
  const [dealAmount, setDealAmount] = useState("");
  const [isBarter, setIsBarter] = useState(false); // Barter/Product exchange
  const [sendingRequest, setSendingRequest] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [upgradePrompt, setUpgradePrompt] = useState({ open: false, used: 3, limit: 3 });
  
  // Filter state
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState(""); // Search by name or city
  // SIP-style filter applied values (only update when user hits Apply)
  const [appliedFilters, setAppliedFilters] = useState({
    minFollowers: FOLLOWERS_MIN,
    maxFollowers: FOLLOWERS_MAX,
    minBudget: BUDGET_MIN,
    maxBudget: BUDGET_MAX,
    radiusKm: null,
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  // Geolocation state
  const [centerLocation, setCenterLocation] = useState(null); // { lat, lng, label }
  const [detectingLocation, setDetectingLocation] = useState(false);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser does not support location services");
      return;
    }
    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        // Try reverse-geocoding via free Nominatim (best-effort, no key)
        let label = "your location";
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`
          );
          const data = await r.json();
          label =
            data?.address?.city ||
            data?.address?.town ||
            data?.address?.village ||
            data?.address?.state_district ||
            data?.address?.state ||
            "your location";
        } catch (_) {}
        setCenterLocation({ lat: latitude, lng: longitude, label });
        setDetectingLocation(false);
        toast.success(`Location set: ${label}`);
      },
      (err) => {
        setDetectingLocation(false);
        toast.error(err.code === 1 ? "Location permission denied" : "Could not detect location");
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };
  
  // Portfolio state
  const [portfolio, setPortfolio] = useState({ items: [] });
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
  const [showMediaViewer, setShowMediaViewer] = useState(false);

  // Categories config - Influencers first, Voice Artists last
  const categories = [
    { id: "all", name: "All", color: "#FF69B4", icon: Users },
    { id: "influencers", name: "Influencers", color: "#E91E63", icon: Smartphone },
    { id: "models", name: "Models", color: "#FF4D6D", icon: Camera },
    { id: "actors", name: "Actors", color: "#7B2FBE", icon: Film },
    { id: "dancers", name: "Dancers", color: "#FF6B00", icon: Music },
    { id: "photographers", name: "Photographers", color: "#00A86B", icon: Camera },
    { id: "editors", name: "Editors", color: "#14B8A6", icon: Clapperboard },
    { id: "gaming", name: "Gaming", color: "#8B5CF6", icon: Gamepad2 },
    { id: "content_writers", name: "Content Writers", color: "#FFD700", icon: PenTool },
    { id: "voice_artists", name: "Voice Artists", color: "#1A73E8", icon: Mic }
  ];

  useEffect(() => {
    fetchTalents();
  }, [selectedCategory, searchQuery, appliedFilters, centerLocation]);

  // Load selected talent image when modal opens
  useEffect(() => {
    if (selectedTalent?.photos?.[0] || selectedTalent?.picture) {
      loadTalentImage(selectedTalent.photos?.[0] || selectedTalent.picture);
    } else {
      setSelectedTalentImageUrl(null);
    }
    
    return () => {
      if (selectedTalentImageUrl && selectedTalentImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(selectedTalentImageUrl);
      }
    };
  }, [selectedTalent]);

  const loadTalentImage = async (picturePath) => {
    if (!picturePath) return;
    if (picturePath.startsWith("http")) {
      setSelectedTalentImageUrl(picturePath);
    } else {
      try {
        const response = await axios.get(`${API}/files/${picturePath}`, {
          responseType: 'blob'
        });
        const blobUrl = URL.createObjectURL(response.data);
        setSelectedTalentImageUrl(blobUrl);
      } catch (error) {
        console.error("Failed to load image:", error);
        setSelectedTalentImageUrl(null);
      }
    }
  };

  const fetchTalents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== "all") params.append("category", selectedCategory);
      if (searchQuery) params.append("search", searchQuery); // Search by name or city

      // Followers range — only send if user narrowed from defaults
      if (appliedFilters.minFollowers > FOLLOWERS_MIN) {
        params.append("min_followers", String(appliedFilters.minFollowers));
      }
      if (appliedFilters.maxFollowers < FOLLOWERS_MAX) {
        params.append("max_followers", String(appliedFilters.maxFollowers));
      }

      // Budget range — only send if user narrowed from defaults
      if (appliedFilters.minBudget > BUDGET_MIN) {
        params.append("min_rate", String(appliedFilters.minBudget));
      }
      if (appliedFilters.maxBudget < BUDGET_MAX) {
        params.append("max_rate", String(appliedFilters.maxBudget));
      }

      // Geo (radius) filter — only when both location and radius are set
      if (centerLocation && appliedFilters.radiusKm) {
        params.append("near_lat", String(centerLocation.lat));
        params.append("near_lng", String(centerLocation.lng));
        params.append("radius_km", String(appliedFilters.radiusKm));
      }

      params.append("limit", "50");
      
      const response = await axios.get(`${API}/talents?${params.toString()}`);
      setTalents(response.data.talents || []);
    } catch (error) {
      console.error("Failed to fetch talents");
      toast.error("Failed to load talents");
    } finally {
      setLoading(false);
    }
  };

  const activeFilterCount =
    (centerLocation && appliedFilters.radiusKm ? 1 : 0) +
    (appliedFilters.minFollowers > FOLLOWERS_MIN || appliedFilters.maxFollowers < FOLLOWERS_MAX ? 1 : 0) +
    (appliedFilters.minBudget > BUDGET_MIN || appliedFilters.maxBudget < BUDGET_MAX ? 1 : 0);

  const clearAllFilters = () => {
    setAppliedFilters({
      minFollowers: FOLLOWERS_MIN,
      maxFollowers: FOLLOWERS_MAX,
      minBudget: BUDGET_MIN,
      maxBudget: BUDGET_MAX,
      radiusKm: null,
    });
    setSearchQuery("");
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  const handleViewProfile = async (talent) => {
    setSelectedTalent(talent);
    setShowProfileModal(true);
    setPortfolio({ items: [] });
    
    // Fetch portfolio
    setLoadingPortfolio(true);
    try {
      const response = await axios.get(`${API}/influencer/${talent.user_id}/portfolio`);
      setPortfolio(response.data);
    } catch (error) {
      setPortfolio({ items: [] });
    } finally {
      setLoadingPortfolio(false);
    }
  };
  
  const openMediaViewer = (index) => {
    setSelectedMediaIndex(index);
    setShowMediaViewer(true);
  };
  
  const navigateMedia = (direction) => {
    const totalItems = portfolio.items?.length || 0;
    if (totalItems === 0) return;
    
    setSelectedMediaIndex(prev => {
      if (direction === 'next') {
        return (prev + 1) % totalItems;
      } else {
        return prev === 0 ? totalItems - 1 : prev - 1;
      }
    });
  };
  
  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/files/')) {
      return `${API.replace('/api', '')}${url}`;
    }
    return `${API}/files/${url}`;
  };

  const getTalentImageUrl = (talent) => {
    // Use ONLY picture field, NOT photos array (photos = portfolio/work samples)
    const photo = talent.picture;
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${API}/files/${photo}`;
  };

  const handleHireClick = (talent) => {
    if (user?.approval_status === "pending") {
      toast.error("Your account is pending approval. Please wait for admin approval.");
      return;
    }
    setSelectedTalent(talent);
    setCollaborateMessage("");
    setDealAmount(""); // Brand decides the amount
    setShowCollabModal(true);
  };

  const handleSendCollaboration = async () => {
    if (!selectedTalent) return;
    
    setSendingRequest(true);
    try {
      await axios.post(`${API}/brand/collaborate`, {
        influencer_id: selectedTalent.user_id,
        message: collaborateMessage,
        deal_amount: dealAmount ? parseInt(dealAmount) : null,
        is_barter: isBarter
      }, { headers: getAuthHeaders() });
      
      toast.success("Hire request sent to " + selectedTalent.name + "!");
      setShowCollabModal(false);
      setSelectedTalent(null);
      setCollaborateMessage("");
      setDealAmount("");
      setIsBarter(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402 && detail && typeof detail === "object" && detail.code === "BRAND_UPGRADE_REQUIRED") {
        setShowCollabModal(false);
        setUpgradePrompt({ open: true, used: detail.used ?? 3, limit: detail.limit ?? 3 });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to send request"));
      }
    } finally {
      setSendingRequest(false);
    }
  };

  const toggleFavorite = (talentId) => {
    setFavorites(prev => 
      prev.includes(talentId) 
        ? prev.filter(id => id !== talentId)
        : [...prev, talentId]
    );
  };

  const formatRate = (rate) => {
    if (!rate) return "N/A";
    if (rate >= 100000) return `₹${(rate / 100000).toFixed(0)}L`;
    if (rate >= 1000) return `₹${(rate / 1000).toFixed(0)}K`;
    return `₹${rate}`;
  };

  const getCategoryConfig = (categoryId) => {
    return categories.find(c => c.id === categoryId) || categories[0];
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] flex items-center justify-center">
              <Star className="w-4 h-4 text-white" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">FOUNDSTAR</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 hidden sm:block">Hi, {user?.name}</span>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              className="text-gray-500 hover:text-[#FF69B4]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Category Filter Pills */}
      <div className="bg-white border-b border-gray-100 sticky top-14 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => {
              const IconComponent = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-[#FF69B4] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`category-pill-${cat.id}`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span className="text-sm font-medium">{cat.name}</span>
                </button>
              );
            })}
          </div>

          {/* SIP-Calculator-style Filter toggle */}
          <div className="flex items-center justify-between gap-2 pt-2">
            <button
              onClick={() => setShowFilterPanel((s) => !s)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
                showFilterPanel || activeFilterCount > 0
                  ? "bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] text-white border-transparent shadow-md"
                  : "bg-white text-gray-700 border-gray-200 hover:border-[#7B2FBE]"
              }`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-3.5 h-3.5" />
              {showFilterPanel ? "Hide Filters" : "Advanced Filters"}
              {activeFilterCount > 0 && (
                <span className={`ml-1 rounded-full px-1.5 text-[10px] font-bold ${
                  showFilterPanel || activeFilterCount > 0 ? "bg-white text-[#7B2FBE]" : "bg-[#FF69B4] text-white"
                }`}>
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-xs font-medium text-gray-500 hover:text-[#FF69B4] flex items-center gap-1"
                data-testid="clear-filters-btn"
              >
                <X className="w-3 h-3" /> Clear all
              </button>
            )}
          </div>

          {/* SIP-Calculator-style filter panel */}
          {showFilterPanel && (
            <div className="mt-3 pb-2" data-testid="sip-filter-panel">
              <SipStyleFilter
                categories={categories}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                filters={appliedFilters}
                onApply={(f) => {
                  setAppliedFilters(f);
                }}
                onReset={() => {
                  setAppliedFilters({
                    minFollowers: FOLLOWERS_MIN,
                    maxFollowers: FOLLOWERS_MAX,
                    minBudget: BUDGET_MIN,
                    maxBudget: BUDGET_MAX,
                    radiusKm: null,
                  });
                }}
                resultCount={talents.length}
                title="Find your perfect star"
                variant="light"
                compact
                centerLocation={centerLocation}
                onRequestLocation={requestLocation}
                detectingLocation={detectingLocation}
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Pending Approval Banner */}
        {user?.approval_status === "pending" && (
          <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Clock className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Account Pending Approval</p>
                <p className="text-amber-700 text-sm">You can browse talents but cannot send hire requests yet.</p>
              </div>
            </div>
          </div>
        )}

        {/* Title & Count */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-outfit font-bold text-2xl text-[#1E293B]">
              {selectedCategory === "all" ? "All Stars" : getCategoryConfig(selectedCategory).name}
            </h1>
            <p className="text-gray-500 text-sm">{talents.length} talents found</p>
          </div>
          
          {/* City Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search name or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-48 rounded-full bg-gray-100 border-0"
            />
          </div>
        </div>

        {/* Talent Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-[3/4] bg-gray-200"></div>
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : talents.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No talents found</h3>
            <p className="text-gray-400">Try changing the category or search filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="talents-grid">
            {talents.map((talent) => {
              const catConfig = getCategoryConfig(talent.category);
              const imageUrl = getTalentImageUrl(talent);
              const isFavorite = favorites.includes(talent.user_id);
              
              return (
                <div 
                  key={talent.user_id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                >
                  {/* Image Container */}
                  <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-pink-100 to-purple-100">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={talent.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-6xl">{catConfig.icon === Camera ? "📸" : "👤"}</span>
                      </div>
                    )}
                    
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex flex-col gap-2">
                      {talent.is_verified && (
                        <Badge className="bg-[#7B2FBE] text-white text-xs px-2 py-1">
                          <BadgeCheck className="w-3 h-3 mr-1" />
                          Pro Creator
                        </Badge>
                      )}
                      {talent.is_featured && (
                        <Badge className="bg-[#FFD700] text-[#1E293B] text-xs px-2 py-1">
                          <Crown className="w-3 h-3 mr-1" />
                          Featured
                        </Badge>
                      )}
                    </div>
                    
                    {/* Favorite Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(talent.user_id); }}
                      className="absolute top-3 right-3 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                    >
                      <Heart 
                        className={`w-5 h-5 ${isFavorite ? 'fill-[#FF69B4] text-[#FF69B4]' : 'text-gray-400'}`} 
                      />
                    </button>
                    
                    {/* Category Badge */}
                    <div className="absolute bottom-3 left-3">
                      <Badge 
                        style={{ backgroundColor: catConfig.color }}
                        className="text-white text-xs px-3 py-1 font-medium"
                      >
                        {catConfig.name}
                      </Badge>
                    </div>
                    
                    {/* Portfolio Thumbnails - Show if photos exist */}
                    {talent.photos && talent.photos.length > 0 && (
                      <div className="absolute bottom-3 right-3 flex gap-1">
                        {talent.photos.slice(0, 3).map((photo, idx) => (
                          <div key={idx} className="w-8 h-8 rounded-md overflow-hidden border-2 border-white shadow-md">
                            <img 
                              src={photo.startsWith('http') ? photo : `${API}/files/${photo}`}
                              alt={`Work ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                        {talent.photos.length > 3 && (
                          <div className="w-8 h-8 rounded-md bg-black/60 border-2 border-white shadow-md flex items-center justify-center">
                            <span className="text-white text-xs font-bold">+{talent.photos.length - 3}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="p-4">
                    {/* Name & Trust Score */}
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-[#1E293B] truncate">{talent.name}</h3>
                      <div className="flex items-center gap-1 bg-[#FFF8E1] px-2 py-0.5 rounded-full">
                        <Star className="w-3 h-3 text-[#FFD700] fill-[#FFD700]" />
                        <span className="text-xs font-medium text-[#1E293B]">
                          {talent.trust_score?.toFixed(1) || talent.rating?.toFixed(1) || "4.5"}
                        </span>
                      </div>
                    </div>
                    
                    {/* Location & Followers Row */}
                    <div className="flex items-center justify-between mb-2">
                      {talent.city ? (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {talent.city}
                        </p>
                      ) : <span></span>}
                      
                      {talent.followers_count > 0 && (
                        <p className="text-xs text-[#7B2FBE] font-medium flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {talent.followers_count >= 1000000 
                            ? `${(talent.followers_count / 1000000).toFixed(1)}M`
                            : talent.followers_count >= 1000 
                              ? `${(talent.followers_count / 1000).toFixed(0)}K`
                              : talent.followers_count}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        onClick={() => handleHireClick(talent)}
                        className="flex-1 bg-[#FF69B4] hover:bg-[#E05297] text-white rounded-full text-sm"
                        size="sm"
                      >
                        Hire Now
                      </Button>
                      <button
                        onClick={() => handleViewProfile(talent)}
                        className="w-10 h-10 rounded-full border-2 border-[#87CEEB] text-[#87CEEB] flex items-center justify-center hover:bg-[#87CEEB] hover:text-white transition-colors"
                      >
                        <Play className="w-4 h-4 ml-0.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* View Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {selectedTalent && (
            <>
              <div className="relative">
                <div className="h-32 bg-gradient-to-br from-[#FF69B4] via-[#FF85C0] to-[#87CEEB]"></div>
                <div className="absolute -bottom-16 left-1/2 -translate-x-1/2">
                  <div className="relative">
                    <Avatar className="w-32 h-32 border-4 border-white shadow-xl">
                      <AvatarImage src={selectedTalentImageUrl || getTalentImageUrl(selectedTalent)} alt={selectedTalent.name} className="object-cover" />
                      <AvatarFallback className="p-0 bg-transparent overflow-hidden">
                        <StarAvatarFallback iconClassName="w-14 h-14" />
                      </AvatarFallback>
                    </Avatar>
                    {selectedTalent.is_verified && (
                      <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-[#7B2FBE] rounded-full flex items-center justify-center border-2 border-white">
                        <BadgeCheck className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => setShowProfileModal(false)}
                  className="absolute top-3 right-3 w-8 h-8 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="pt-20 px-6 pb-6">
                <div className="text-center mb-4">
                  <h3 className="font-outfit font-bold text-2xl text-[#1E293B]">{selectedTalent.name}</h3>
                  <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                    <Badge style={{ backgroundColor: getCategoryConfig(selectedTalent.category).color }} className="text-white">
                      {getCategoryConfig(selectedTalent.category).name}
                    </Badge>
                    {selectedTalent.city && (
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {selectedTalent.city}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3 text-center">
                    <Users className="w-5 h-5 text-[#7B2FBE] mx-auto mb-1" />
                    <p className="font-bold text-[#1E293B]">
                      {selectedTalent.followers_count >= 1000000 
                        ? `${(selectedTalent.followers_count / 1000000).toFixed(1)}M`
                        : selectedTalent.followers_count >= 1000 
                          ? `${(selectedTalent.followers_count / 1000).toFixed(0)}K`
                          : selectedTalent.followers_count || "0"}
                    </p>
                    <p className="text-xs text-gray-500">Followers</p>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-3 text-center">
                    <Star className="w-5 h-5 text-[#FFD700] mx-auto mb-1 fill-[#FFD700]" />
                    <p className="font-bold text-[#1E293B]">
                      {selectedTalent.trust_score?.toFixed(1) || selectedTalent.rating?.toFixed(1) || "4.5"}
                    </p>
                    <p className="text-xs text-gray-500">Trust Score</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-3 text-center">
                    <BadgeCheck className="w-5 h-5 text-[#00A86B] mx-auto mb-1" />
                    <p className="font-bold text-[#1E293B]">{selectedTalent.closed_deals || selectedTalent.total_reviews || 0}</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </div>
                </div>
                
                {selectedTalent.bio && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-5">
                    <p className="text-gray-600 text-sm text-center italic">"{selectedTalent.bio}"</p>
                  </div>
                )}

                {/* Portfolio */}
                <div className="mb-5">
                  <h4 className="font-semibold text-[#1E293B] mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4 text-[#FF69B4]" />
                    Portfolio
                  </h4>
                  
                  {loadingPortfolio ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-3 border-[#FF69B4] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : portfolio.items?.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {portfolio.items.slice(0, 6).map((item, index) => (
                        <div 
                          key={item.item_id || index}
                          onClick={() => openMediaViewer(index)}
                          className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
                        >
                          {item.type === 'video' ? (
                            <>
                              <video src={getMediaUrl(item.url)} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <Play className="w-8 h-8 text-white" />
                              </div>
                            </>
                          ) : (
                            <img src={getMediaUrl(item.url)} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-6 text-center">
                      <p className="text-sm text-gray-400">No portfolio items yet</p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => {
                    setShowProfileModal(false);
                    handleHireClick(selectedTalent);
                  }}
                  className="w-full bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] hover:opacity-90 rounded-full py-6 text-lg font-semibold"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Send Hire Request
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Media Viewer */}
      <Dialog open={showMediaViewer} onOpenChange={setShowMediaViewer}>
        <DialogContent className="sm:max-w-4xl p-0 bg-black/95 border-0">
          {portfolio.items?.[selectedMediaIndex] && (
            <div className="relative">
              <button 
                onClick={() => setShowMediaViewer(false)}
                className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
              >
                <X className="w-6 h-6" />
              </button>
              
              {portfolio.items.length > 1 && (
                <>
                  <button 
                    onClick={() => navigateMedia('prev')}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                  <button 
                    onClick={() => navigateMedia('next')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                </>
              )}
              
              <div className="flex items-center justify-center min-h-[60vh] p-8">
                {portfolio.items[selectedMediaIndex].type === 'video' ? (
                  <video src={getMediaUrl(portfolio.items[selectedMediaIndex].url)} controls autoPlay className="max-w-full max-h-[70vh] rounded-lg" />
                ) : (
                  <img src={getMediaUrl(portfolio.items[selectedMediaIndex].url)} alt="" className="max-w-full max-h-[70vh] rounded-lg object-contain" />
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hire Request Modal */}
      <Dialog open={showCollabModal} onOpenChange={setShowCollabModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Send Hire Request</DialogTitle>
          </DialogHeader>
          
          {selectedTalent && (
            <div className="mt-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl mb-4">
                <Avatar className="w-14 h-14">
                  <AvatarImage src={getTalentImageUrl(selectedTalent)} />
                  <AvatarFallback className="p-0 bg-transparent overflow-hidden">
                    <StarAvatarFallback iconClassName="w-6 h-6" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#1E293B]">{selectedTalent.name}</span>
                    {selectedTalent.is_verified && (
                      <BadgeCheck className="w-4 h-4 text-[#7B2FBE]" />
                    )}
                  </div>
                  <Badge style={{ backgroundColor: getCategoryConfig(selectedTalent.category).color }} className="text-white text-xs">
                    {getCategoryConfig(selectedTalent.category).name}
                  </Badge>
                </div>
              </div>

              <div className="mb-4">
                <label className="text-sm text-gray-500 mb-1 block">Your Message (Optional)</label>
                <Textarea
                  placeholder="Hi! We'd love to work with you on our upcoming project..."
                  value={collaborateMessage}
                  onChange={(e) => setCollaborateMessage(e.target.value)}
                  className="rounded-xl min-h-[100px]"
                />
              </div>

              <div className="mb-4">
                <label className="text-sm text-gray-500 mb-1 block">
                  {isBarter ? "Product Value (₹)" : "Budget (₹)"}
                </label>
                <Input
                  type="number"
                  placeholder={isBarter ? "Enter product value" : "Enter your budget"}
                  value={dealAmount}
                  onChange={(e) => setDealAmount(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              {/* Barter Toggle */}
              <div className="mb-4 flex items-center gap-3 p-3 bg-purple-50 rounded-xl border border-purple-100">
                <input
                  type="checkbox"
                  id="barter-toggle"
                  checked={isBarter}
                  onChange={(e) => setIsBarter(e.target.checked)}
                  className="w-5 h-5 text-purple-600 rounded"
                />
                <label htmlFor="barter-toggle" className="flex-1 cursor-pointer">
                  <span className="font-medium text-purple-800">Barter Collaboration</span>
                  <p className="text-xs text-purple-600">Send product instead of payment</p>
                </label>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowCollabModal(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendCollaboration}
                  disabled={sendingRequest}
                  className="flex-1 bg-[#FF69B4] hover:bg-[#E05297] rounded-full"
                >
                  {sendingRequest ? "Sending..." : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Request
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav userType="brand" />

      <ProUpgradePrompt
        open={upgradePrompt.open}
        used={upgradePrompt.used}
        limit={upgradePrompt.limit}
        onClose={() => setUpgradePrompt({ ...upgradePrompt, open: false })}
        onUpgrade={() => {
          setUpgradePrompt({ ...upgradePrompt, open: false });
          navigate("/brand/profile");
        }}
      />
    </div>
  );
};

export default BrandHome;
